/**
 * The player's breadcrumb trail (design D1): the dog does not know the maze, it knows
 * where the player has walked. A point is dropped every `TRAIL_SPACING` metres travelled;
 * the follower eats the queue from the front, steering at whatever is now at its head, and
 * when the queue runs out steers straight at the player and stops at `STOP_RADIUS`.
 *
 * Pure: plain `Vec3`s in and out, no three.js, no Rapier, no store import — `src/game/`
 * is renderer-agnostic and headlessly testable, architectural rule 1.
 */

import type { Vec3 } from './profile/types';

/**
 * Correctness, not tuning: the chord between two consecutive points is at most this plus
 * one frame of sprint (~0.44m, ~0.55m after a long frame). A corridor is 2.4m and a crest
 * 0.6m, so a chord cut across a corner never leaves the space the player actually walked.
 */
export const TRAIL_SPACING = 0.35;

/** ~90m of trail. Past this the front is dropped: a hopelessly-lagging dog cuts to what is
 * left rather than retracing history. */
export const MAX_TRAIL_POINTS = 256;

/** Close enough that the dog has "reached" a trail point. Looped, so a tight cluster of
 * points is eaten in one frame rather than one per frame. */
export const ARRIVE_RADIUS = 0.3;

/** Close enough to the player to be company; far enough to stay out of the camera. */
export const STOP_RADIUS = 1.1;

/** Hysteresis on STOP_RADIUS, so a resting dog does not stutter in and out of walking as
 * the player shifts half a step. */
export const RESUME_RADIUS = 1.5;

/** The queue of points the player has dropped, oldest first. */
export type Trail = { points: Vec3[] };

export function createTrail(): Trail {
  return { points: [] };
}

function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

/**
 * Drops a new point at the back of the queue, unless the player has not yet gone
 * `TRAIL_SPACING` past the last one — except the very first point, which always lands,
 * since there is nothing yet to measure it against.
 */
export function pushTrailPoint(trail: Trail, point: Vec3): void {
  const last = trail.points[trail.points.length - 1];
  if (last && distance(last, point) < TRAIL_SPACING) return;

  trail.points.push({ x: point.x, y: point.y, z: point.z });
  if (trail.points.length > MAX_TRAIL_POINTS) trail.points.shift();
}

/**
 * Eats the queue from the front by the two rules of D1: a point the follower has reached,
 * and a point the player is already standing at (which is what stops a dog resting at its
 * owner's heel from silting the queue up, and what erases most of a circle walked around
 * a standing dog).
 */
export function pruneTrail(trail: Trail, followerPosition: Vec3, playerPosition: Vec3): void {
  while (trail.points.length > 0) {
    const front = trail.points[0]!;
    const reached = distance(front, followerPosition) <= ARRIVE_RADIUS;
    const atPlayer = distance(front, playerPosition) <= STOP_RADIUS;
    if (!reached && !atPlayer) break;
    trail.points.shift();
  }
}

/**
 * How far behind the follower is, in metres of corridor rather than of straight line — the
 * queue already encodes the right measure for free: its length times the spacing it was
 * laid down at, plus the last stretch to the front point. Falls back to the straight-line
 * distance to the player once the queue is empty, which is the only target left to measure.
 */
export function trailBacklog(trail: Trail, followerPosition: Vec3, playerPosition: Vec3): number {
  if (trail.points.length === 0) return distance(followerPosition, playerPosition);
  return trail.points.length * TRAIL_SPACING + distance(followerPosition, trail.points[0]!);
}

/** Empties the queue — used when the follow lifecycle restarts (design D3, D5). */
export function clearTrail(trail: Trail): void {
  trail.points.length = 0;
}
